import { useDisclosure, useLocalStorage } from '@mantine/hooks';
import useFeatureFlags from '@modules/core/hooks/useFeatureFlags';
import { PendingComponent } from '@modules/core/components/PendingComponent';
import { NotFoundComponent } from '@modules/core/components/NotFoundComponent';
import { useRouterState } from '@tanstack/react-router';
import usePersistUser from '@modules/core/hooks/usePersistUser';
import useGetBrokerProfile from '@modules/core/hooks/queries/useGetBrokerProfile';
import useGetSidebarLinks from '@modules/core/layout/hooks/useGetSidebarLinks';
import { LayoutV2 } from '@modules/core/presentation/v2/layout';

type Props = {
  children: React.ReactNode;
};

export const Layout = ({ children }: Props) => {
  const [sidebarCollapsed, setSideBarCollapsed] = useLocalStorage({
    key: 'sidebarCollapsed',
    defaultValue: false,
    getInitialValueInEffect: false,
  });
  const [, { toggle: toggleCollapse }] = useDisclosure(
    sidebarCollapsed,
    { onOpen: () => setSideBarCollapsed(true), onClose: () => setSideBarCollapsed(false) }
  );

  const { user } = usePersistUser();
  const { data: brokerProfile } = useGetBrokerProfile(user?.id);

  const { sidebarLinks } = useGetSidebarLinks();
  const { isFeatureFlagsLoading, isRefetching, isFeatureActive } = useFeatureFlags();
  const currentRoute = useRouterState({ select: (s) => s.matches }).findLast(Boolean)!;
  const isLoading = isFeatureFlagsLoading || isRefetching;

  let componentToRender = children;
  if (isLoading) {
    componentToRender = <PendingComponent />;
  }
  if (currentRoute.context?.featureName && !isLoading && !isFeatureActive(currentRoute.context.featureName)) {
    componentToRender = <NotFoundComponent />;
  }

  return (
    <LayoutV2
      sidebarCollapsed={ sidebarCollapsed }
      toggleCollapse={ toggleCollapse }
      sidebarLinks={ sidebarLinks }
      hasAcceptedTnC={ brokerProfile?.terms_conditions_version === TERMS_AND_CONDITIONS_VERSION }
      hasAccessToPortal={ brokerProfile?.is_granted_access_to_mb_portal }
    >
      {componentToRender}
    </LayoutV2>
  );
};
